import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loader2, Save, Plus, Trash2, Rss, KeyRound, ShieldPlus, Sunrise, Copy, Share2, Eye } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { AdminCrud, ImageField } from './AdminCrud';
import { api, getUser, fileUrl } from '../../lib/api';
import { toast } from 'sonner';

const DEFAULT_TAGLINE_BUTTONS = [
  { label: 'Donate', url: '/contact', style: 'gold' },
  { label: 'Volunteer', url: '/volunteer', style: 'outline' },
];

const SettingsForm = ({ settingKey, fields, values, onSaved }) => {
  const [form, setForm] = useState(values || {});
  const [busy, setBusy] = useState(false);
  useEffect(() => { setForm(values || {}); }, [values]);

  const save = async () => {
    setBusy(true);
    try {
      await api.put('/settings', { key: settingKey, value: form });
      toast.success('Saved');
      onSaved?.();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card><CardContent className="p-5 space-y-4">
      {fields.map((f) => (
        <div key={f.name}>
          <label className="block font-bold text-[#1a2744] mb-1.5 text-sm">{f.label}</label>
          {f.type === 'textarea' ? (
            <Textarea value={form[f.name] || ''} onChange={(e) => setForm((p) => ({ ...p, [f.name]: e.target.value }))} rows={3} data-testid={`setting-${settingKey}-${f.name}`} />
          ) : f.type === 'select' ? (
            <Select value={form[f.name] || f.default || ''} onValueChange={(v) => setForm((p) => ({ ...p, [f.name]: v }))}>
              <SelectTrigger className="h-11" data-testid={`setting-${settingKey}-${f.name}`}><SelectValue /></SelectTrigger>
              <SelectContent>{f.options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
            </Select>
          ) : (
            <Input value={form[f.name] || ''} onChange={(e) => setForm((p) => ({ ...p, [f.name]: e.target.value }))} className="h-11" data-testid={`setting-${settingKey}-${f.name}`} />
          )}
          {f.help && <p className="text-xs text-slate-400 mt-1">{f.help}</p>}
        </div>
      ))}
      <Button onClick={save} disabled={busy} className="h-11 bg-[#1a2744] text-white font-bold btn-press" data-testid={`setting-${settingKey}-save`}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />} Save
      </Button>
    </CardContent></Card>
  );
};

const UsersTab = () => {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: '', password: '', name: '', role: 'editor' });
  const me = getUser();

  const load = () => api.get('/auth/users').then((r) => setUsers(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post('/auth/users', form);
      toast.success('User created — they must change their password on first login');
      setOpen(false);
      setForm({ email: '', password: '', name: '', role: 'editor' });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create user');
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/auth/users/${id}`);
      toast.success('User removed');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  return (
    <div>
      <div className="flex justify-end mb-3">
        <Button onClick={() => setOpen(true)} className="h-10 bg-[#1a2744] text-white font-bold btn-press" data-testid="users-add-button"><Plus className="h-4 w-4 mr-1" /> Add User</Button>
      </div>
      <div className="space-y-2">
        {users.map((u) => (
          <Card key={u.id} data-testid="user-row"><CardContent className="p-4 flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-[#1a2744]">{u.name || u.email} {u.id === me?.id && <span className="text-xs text-slate-400">(you)</span>}</p>
              <p className="text-xs text-slate-500">{u.email} · <span className="font-bold uppercase">{u.role}</span></p>
            </div>
            <Select value={u.role} onValueChange={async (v) => { await api.put(`/auth/users/${u.id}`, { role: v }); load(); }}>
              <SelectTrigger className="h-9 w-28"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="admin">Admin</SelectItem><SelectItem value="editor">Editor</SelectItem></SelectContent>
            </Select>
            {u.id !== me?.id && <button onClick={() => remove(u.id)} className="text-red-600 p-2 rounded hover:bg-red-50" aria-label="Delete user" data-testid="user-delete-button"><Trash2 className="h-4 w-4" /></button>}
          </CardContent></Card>
        ))}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="user-create-dialog">
          <DialogHeader><DialogTitle className="font-serif text-[#1a2744]">Add admin user</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Name" className="h-11" data-testid="user-name-input" />
            <Input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} placeholder="Email" className="h-11" data-testid="user-email-input" />
            <Input type="password" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} placeholder="Temporary password (min 8 chars)" className="h-11" data-testid="user-password-input" />
            <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
              <SelectTrigger className="h-11" data-testid="user-role-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="editor">Editor (content only)</SelectItem><SelectItem value="admin">Admin (full access)</SelectItem></SelectContent>
            </Select>
            <Button onClick={create} className="w-full h-11 bg-[#1a2744] text-white font-bold btn-press" data-testid="user-create-button">Create User</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ------------------------------------------------ "Lesperance is the French word for Hope" theme editor ----
const HopeBlockCard = ({ label, block = {}, onChange, withCta = false, withSubtitle = false, withPoints = false }) => {
  const set = (k, v) => onChange({ ...block, [k]: v });
  return (
    <Card><CardContent className="p-5 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <p className="font-serif font-bold text-[#1a2744] flex items-center gap-2"><Sunrise className="h-4 w-4 text-[#C9A84C]" /> {label}</p>
        <label className="flex items-center gap-2 text-xs font-semibold text-slate-600">Show
          <Switch checked={block.show !== false} onCheckedChange={(v) => set('show', v)} data-testid={`hope-show-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`} />
        </label>
      </div>
      <Input value={block.title || ''} onChange={(e) => set('title', e.target.value)} placeholder="Title" className="h-11" />
      {withSubtitle && <Input value={block.subtitle || ''} onChange={(e) => set('subtitle', e.target.value)} placeholder="Subtitle" className="h-11" />}
      <Textarea value={block.text || ''} onChange={(e) => set('text', e.target.value)} rows={4} placeholder="Body text" />
      {withPoints && (
        <Textarea value={(block.points || []).join('\n')} onChange={(e) => set('points', e.target.value.split('\n').filter((x) => x.trim()))} rows={5} placeholder="Bullet points — one per line" />
      )}
      {withCta && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Input value={block.cta_text || ''} onChange={(e) => set('cta_text', e.target.value)} placeholder="Button text (e.g. Bring Hope to Ward 8)" className="h-11" />
          <Input value={block.cta_link || ''} onChange={(e) => set('cta_link', e.target.value)} placeholder="Button link (e.g. /volunteer, /contact, /platform)" className="h-11" />
        </div>
      )}
    </CardContent></Card>
  );
};

const HopeTab = () => {
  const [hope, setHope] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/admin/settings').then((r) => setHope(r.data?.hope_theme || {})).catch(() => setHope({}));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/settings', { key: 'hope_theme', value: hope });
      toast.success('Hope theme saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  if (!hope) return <p className="text-sm text-slate-500">Loading…</p>;
  const set = (k, v) => setHope({ ...hope, [k]: v });
  const setSub = (k, sub) => setHope({ ...hope, [k]: sub });

  return (
    <div className="space-y-4 max-w-3xl">
      <p className="text-sm text-slate-600 bg-[#f6f4ee] border border-[#C9A84C]/40 rounded-lg p-3">
        <strong className="text-[#1a2744]">Lesperance is the French word for Hope.</strong> {hope.meaning_note || "Lesperance comes from the French idea of hope — l'espérance."} Use this theme in a classy way — warm, hopeful, and strong — without overusing it. The campaign stays focused on accountability, taxpayer protection, public service, and Ward 8.
      </p>

      {/* Hero elements */}
      <Card><CardContent className="p-5 space-y-3">
        <p className="font-serif font-bold text-[#1a2744]">Hero elements</p>
        <div className="flex items-center justify-between gap-3">
          <Input value={hope.hero_badge?.text || ''} onChange={(e) => setSub('hero_badge', { ...hope.hero_badge, text: e.target.value })} placeholder="Hero badge text" className="h-11" data-testid="hope-admin-badge-text" />
          <label className="flex items-center gap-2 text-xs font-semibold text-slate-600 shrink-0">Badge
            <Switch checked={hope.hero_badge?.show !== false} onCheckedChange={(v) => setSub('hero_badge', { ...hope.hero_badge, show: v })} data-testid="hope-admin-badge-toggle" />
          </label>
        </div>
        <div className="flex items-center justify-between gap-3">
          <Input value={hope.hero_subtitle?.text || ''} onChange={(e) => setSub('hero_subtitle', { ...hope.hero_subtitle, text: e.target.value })} placeholder="Optional hero subtitle" className="h-11" data-testid="hope-admin-hero-subtitle" />
          <label className="flex items-center gap-2 text-xs font-semibold text-slate-600 shrink-0">Show
            <Switch checked={!!hope.hero_subtitle?.show} onCheckedChange={(v) => setSub('hero_subtitle', { ...hope.hero_subtitle, show: v })} />
          </label>
        </div>
        <div className="flex items-center justify-between gap-3">
          <Input value={hope.hero_tagline?.text || ''} onChange={(e) => setSub('hero_tagline', { ...hope.hero_tagline, text: e.target.value })} placeholder="Tagline strip under the hero" className="h-11" />
          <label className="flex items-center gap-2 text-xs font-semibold text-slate-600 shrink-0">Show
            <Switch checked={hope.hero_tagline?.show !== false} onCheckedChange={(v) => setSub('hero_tagline', { ...hope.hero_tagline, show: v })} />
          </label>
        </div>
        <div className="pt-1">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-bold text-slate-600">Tagline strip buttons (small — e.g. Donate, Volunteer)</p>
            <Button type="button" variant="outline" size="sm" className="font-bold h-8" onClick={() => setSub('hero_tagline', { ...hope.hero_tagline, buttons: [...(hope.hero_tagline?.buttons || DEFAULT_TAGLINE_BUTTONS), { label: '', url: '', style: 'gold' }] })} data-testid="hope-admin-add-strip-btn"><Plus className="h-3.5 w-3.5 mr-1" /> Add</Button>
          </div>
          <div className="space-y-2">
            {(hope.hero_tagline?.buttons || DEFAULT_TAGLINE_BUTTONS).map((b, i) => {
              const buttons = hope.hero_tagline?.buttons || DEFAULT_TAGLINE_BUTTONS;
              const setBtn = (k, v) => setSub('hero_tagline', { ...hope.hero_tagline, buttons: buttons.map((x, j) => (j === i ? { ...x, [k]: v } : x)) });
              return (
                <div key={i} className="flex gap-2">
                  <Input value={b.label} onChange={(e) => setBtn('label', e.target.value)} placeholder="Donate" className="h-10" data-testid={`hope-admin-strip-btn-label-${i}`} />
                  <Input value={b.url} onChange={(e) => setBtn('url', e.target.value)} placeholder="/contact" className="h-10" />
                  <Select value={b.style || 'gold'} onValueChange={(v) => setBtn('style', v)}>
                    <SelectTrigger className="h-10 w-28"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="gold">Gold</SelectItem>
                      <SelectItem value="outline">Outline</SelectItem>
                    </SelectContent>
                  </Select>
                  <button onClick={() => setSub('hero_tagline', { ...hope.hero_tagline, buttons: buttons.filter((_, j) => j !== i) })} className="text-red-600 p-2" aria-label="Remove button"><Trash2 className="h-4 w-4" /></button>
                </div>
              );
            })}
          </div>
          <p className="text-[11px] text-slate-400 mt-1.5">Note: there is no dedicated donate page yet — Donate points to /contact until one exists.</p>
        </div>
      </CardContent></Card>

      {/* Homepage section + placement */}
      <HopeBlockCard label="Homepage — Lesperance is the French word for Hope" block={hope.homepage} onChange={(b) => setSub('homepage', b)} withCta withSubtitle withPoints />
      <Card><CardContent className="p-4 flex items-center gap-3">
        <p className="text-sm font-bold text-[#1a2744] shrink-0">Homepage placement</p>
        <Select value={hope.homepage?.placement || 'after_big_picture'} onValueChange={(v) => setSub('homepage', { ...hope.homepage, placement: v })}>
          <SelectTrigger className="h-11" data-testid="hope-admin-placement"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="after_hero">Right after the hero</SelectItem>
            <SelectItem value="after_why">After "Why Vote Steve?"</SelectItem>
            <SelectItem value="after_big_picture">After "See the Big Picture" (recommended)</SelectItem>
            <SelectItem value="before_footer">Near the bottom (before volunteer/subscribe)</SelectItem>
          </SelectContent>
        </Select>
      </CardContent></Card>

      <HopeBlockCard label="Homepage map section — A Map of Hope and Accountability" block={hope.map_block} onChange={(b) => setSub('map_block', b)} />
      <HopeBlockCard label="Why I'm Running — Hope With Action" block={hope.why_running} onChange={(b) => setSub('why_running', b)} />
      <HopeBlockCard label="Platform — Hope Through Accountability" block={hope.platform} onChange={(b) => setSub('platform', b)} withPoints />
      <HopeBlockCard label="Ward 8 Public Issue Tracker — Hope You Can Track" block={hope.hub} onChange={(b) => setSub('hub', b)} />

      {/* Slogan bank */}
      <Card><CardContent className="p-5">
        <p className="font-serif font-bold text-[#1a2744] mb-1">Campaign slogan bank</p>
        <p className="text-xs text-slate-500 mb-3">Copy-ready slogans for posts, print, and speeches. One per line.</p>
        <div className="space-y-1.5 mb-3" data-testid="hope-slogan-bank">
          {(hope.slogans || []).map((s, i) => (
            <div key={`${i}-${s.slice(0, 30)}`} className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
              <p className="text-sm text-[#1a2744] flex-1">{s}</p>
              <button onClick={() => { navigator.clipboard?.writeText(s); toast.success('Slogan copied'); }} className="text-slate-400 hover:text-[#1a2744]" title="Copy" data-testid={`hope-slogan-copy-${i}`}><Copy className="h-4 w-4" /></button>
              <button onClick={() => set('slogans', hope.slogans.filter((_, j) => j !== i))} className="text-red-400 hover:text-red-600" title="Remove"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <Input id="new-slogan" placeholder="Add a slogan…" className="h-11" data-testid="hope-slogan-input"
            onKeyDown={(e) => { if (e.key === 'Enter' && e.target.value.trim()) { set('slogans', [...(hope.slogans || []), e.target.value.trim()]); e.target.value = ''; } }} />
          <Button variant="outline" className="h-11 font-bold" data-testid="hope-slogan-add" onClick={() => {
            const el = document.getElementById('new-slogan');
            if (el?.value.trim()) { set('slogans', [...(hope.slogans || []), el.value.trim()]); el.value = ''; }
          }}><Plus className="h-4 w-4 mr-1" /> Add</Button>
        </div>
      </CardContent></Card>

      <Button onClick={save} disabled={saving} className="h-12 px-6 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="hope-admin-save">
        {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />} Save Hope theme
      </Button>
    </div>
  );
};

// ------------------------------------------------ Social Sharing (share preview prep) ----
const SHARING_DEFAULTS = {
  default_title: 'Vote Steve Lesperance — Ward 8 Councillor, Kawartha Lakes',
  default_description: 'Report it. Map it. Track it. Get answers. The Ward 8 Public Issue Tracker and a practical plan for better service and better value.',
  default_image: '/api/uploads/ward8-aerial.jpg',
  issue_tracker_image: '/api/uploads/ward8-aerial.jpg',
  savings_tracker_image: '',
  newsroom_image: '',
  homepage_image: '',
  final_domain: '',
};

const SharingImageRow = ({ label, name, form, set, help }) => (
  <div>
    <label className="block font-bold text-[#1a2744] mb-1.5 text-sm">{label}</label>
    <ImageField value={form[name] || ''} onChange={(v) => set(name, v)} category="sharing" />
    {help && <p className="text-xs text-slate-400 mt-1">{help}</p>}
  </div>
);

const SharingTab = ({ values, onSaved }) => {
  const [form, setForm] = useState({ ...SHARING_DEFAULTS, ...(values || {}) });
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState('default');
  useEffect(() => { setForm({ ...SHARING_DEFAULTS, ...(values || {}) }); }, [values]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      await api.put('/settings', { key: 'social_sharing', value: form });
      toast.success('Social sharing settings saved');
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    setBusy(false);
  };

  const PREVIEW_PAGES = {
    default: { label: 'Default', image: form.default_image },
    homepage: { label: 'Homepage', image: form.homepage_image || form.default_image },
    issue_tracker: { label: 'Issue Tracker', image: form.issue_tracker_image || form.default_image },
    savings_tracker: { label: 'Savings Tracker', image: form.savings_tracker_image || form.default_image },
    newsroom: { label: 'Newsroom', image: form.newsroom_image || form.default_image },
  };
  const pv = PREVIEW_PAGES[preview];
  const domainShown = (form.final_domain || 'final-domain-not-set-yet.com').replace(/^https?:\/\//, '').replace(/\/$/, '');

  return (
    <div className="space-y-4 max-w-3xl">
      <p className="text-sm text-slate-600 bg-[#f6f4ee] border border-[#C9A84C]/40 rounded-lg p-3" data-testid="sharing-prep-note">
        <strong className="text-[#1a2744]">Share preview prep.</strong> These settings get everything ready for Facebook/social link previews.
        They take full effect once the <strong>final public domain</strong> is confirmed and connected — current site OpenGraph tags stay untouched until then.
        The Ward 8 aerial is the preferred Issue Tracker share image.
      </p>

      <Card><CardContent className="p-5 space-y-4">
        <div>
          <label className="block font-bold text-[#1a2744] mb-1.5 text-sm">Default share title</label>
          <Input value={form.default_title} onChange={(e) => set('default_title', e.target.value)} className="h-11" data-testid="sharing-default-title" />
        </div>
        <div>
          <label className="block font-bold text-[#1a2744] mb-1.5 text-sm">Default share description</label>
          <Textarea value={form.default_description} onChange={(e) => set('default_description', e.target.value)} rows={2} data-testid="sharing-default-description" />
        </div>
        <SharingImageRow label="Default share image" name="default_image" form={form} set={set} />
        <SharingImageRow label="Issue Tracker share image" name="issue_tracker_image" form={form} set={set} help="Preferred: the Ward 8 aerial image." />
        <SharingImageRow label="Savings Tracker share image" name="savings_tracker_image" form={form} set={set} />
        <SharingImageRow label="Newsroom share image" name="newsroom_image" form={form} set={set} />
        <SharingImageRow label="Homepage share image" name="homepage_image" form={form} set={set} />
        <div>
          <label className="block font-bold text-[#1a2744] mb-1.5 text-sm">Final public domain</label>
          <Input value={form.final_domain} onChange={(e) => set('final_domain', e.target.value)} placeholder="e.g. stevelesperance.com — leave blank until confirmed" className="h-11" data-testid="sharing-final-domain" />
          <p className="text-xs text-slate-400 mt-1">Leave blank until the final domain is confirmed. Nothing breaks in the meantime.</p>
        </div>
        <Button onClick={save} disabled={busy} className="h-11 bg-[#1a2744] text-white font-bold btn-press" data-testid="sharing-save">
          {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />} Save Sharing Settings
        </Button>
      </CardContent></Card>

      <Card><CardContent className="p-5">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <p className="font-serif font-bold text-[#1a2744] flex items-center gap-2"><Eye className="h-4 w-4 text-[#C9A84C]" /> Share preview / test</p>
          <Select value={preview} onValueChange={setPreview}>
            <SelectTrigger className="h-9 w-44 text-xs" data-testid="sharing-preview-select"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(PREVIEW_PAGES).map(([v, p]) => <SelectItem key={v} value={v}>{p.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="max-w-md rounded-xl border border-slate-300 overflow-hidden bg-white" data-testid="sharing-preview-card">
          {pv.image ? (
            <img src={fileUrl(pv.image)} alt="Share preview" className="w-full h-52 object-cover" data-testid="sharing-preview-image" />
          ) : (
            <div className="w-full h-52 bg-slate-100 flex items-center justify-center text-slate-400 text-sm">No image set — default will be used</div>
          )}
          <div className="p-3 bg-slate-50">
            <p className="text-[10px] uppercase tracking-wide text-slate-500" data-testid="sharing-preview-domain">{domainShown}</p>
            <p className="font-bold text-[#1a2744] text-sm mt-0.5 leading-snug">{form.default_title}</p>
            <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{form.default_description}</p>
          </div>
        </div>
        <p className="text-xs text-slate-400 mt-2">This simulates how the link card will look on Facebook once the final domain is live.</p>
      </CardContent></Card>
    </div>
  );
};

const FeedsTab = () => {
  const [sources, setSources] = useState([]);
  const [form, setForm] = useState({ name: '', type: 'rss', url: '' });
  const [importing, setImporting] = useState(null);
  const [importForm, setImportForm] = useState({ title: '', content: '' });

  const load = () => api.get('/admin/feed-sources').then((r) => setSources(r.data || []));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.name) { toast.error('Name the source'); return; }
    await api.post('/admin/feed-sources', form);
    setForm({ name: '', type: 'rss', url: '' });
    toast.success('Source added');
    load();
  };

  const fetchRss = async (s) => {
    try {
      const res = await api.post(`/admin/feed-sources/${s.id}/fetch-rss`);
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fetch failed');
    }
  };

  const doImport = async () => {
    if (!importForm.title || !importForm.content) { toast.error('Title and content required'); return; }
    const res = await api.post(`/admin/feed-sources/${importing.id}/items`, importForm);
    toast.success(res.data.message);
    setImporting(null);
    setImportForm({ title: '', content: '' });
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3">All imported items are saved as <strong>DRAFT news stories</strong> and must be reviewed and published before appearing publicly. Social platforms (Facebook, Instagram, X, Threads) are <strong>manual import / official embed / API only</strong> — no scraping that violates platform rules.</p>
      <Card><CardContent className="p-4 flex flex-col sm:flex-row gap-2">
        <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Source name" className="h-11" data-testid="feed-name-input" />
        <Select value={form.type} onValueChange={(v) => setForm((f) => ({ ...f, type: v }))}>
          <SelectTrigger className="h-11 sm:w-44" data-testid="feed-type-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="rss">RSS feed</SelectItem>
            <SelectItem value="url">Official page link</SelectItem>
            <SelectItem value="social">Social post (paste)</SelectItem>
            <SelectItem value="video">YouTube/Rumble link</SelectItem>
            <SelectItem value="pdf">Uploaded PDF</SelectItem>
          </SelectContent>
        </Select>
        <Input value={form.url} onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))} placeholder="URL (optional for paste)" className="h-11" data-testid="feed-url-input" />
        <Button onClick={add} className="h-11 bg-[#1a2744] text-white font-bold shrink-0 btn-press" data-testid="feed-add-button"><Plus className="h-4 w-4 mr-1" /> Add</Button>
      </CardContent></Card>
      <div className="space-y-2">
        {sources.map((s) => (
          <Card key={s.id} data-testid="feed-source-row"><CardContent className="p-4 flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-[#1a2744]">{s.name} {s.election_source && <span className="ml-1 text-[10px] font-bold uppercase tracking-wide text-[#8a6f22] bg-[#f6f4ee] border border-[#e5dcc0] rounded px-1.5 py-0.5">Election source</span>}</p>
              <p className="text-xs text-slate-500 truncate">{s.type}{s.platform ? ` · ${s.platform}` : ''}{s.import_method ? ` · import: ${s.import_method}` : ''} {s.url && `· ${s.url}`}</p>
              <p className="text-[11px] text-slate-400">
                Draft-first: <strong className="text-emerald-700">{s.draft_first === false ? 'no' : 'yes'}</strong> · Auto-import: <strong>{s.auto_import ? 'yes' : 'no'}</strong>
                {s.status ? ` · Status: ${s.status}` : ''}{s.last_checked ? ` · Last checked: ${s.last_checked.slice(0, 10)}` : ''}
              </p>
            </div>
            {s.type === 'rss' && <Button variant="outline" size="sm" className="font-bold" onClick={() => fetchRss(s)} data-testid="feed-fetch-rss"><Rss className="h-3.5 w-3.5 mr-1" /> Fetch → Drafts</Button>}
            <Button variant="outline" size="sm" className="font-bold" onClick={() => setImporting(s)} data-testid="feed-paste-import">Paste → Draft</Button>
            <button onClick={async () => { await api.delete(`/admin/feed-sources/${s.id}`); load(); }} className="text-red-600 p-2 rounded hover:bg-red-50" aria-label="Delete"><Trash2 className="h-4 w-4" /></button>
          </CardContent></Card>
        ))}
        {sources.length === 0 && <Card><CardContent className="p-6 text-center text-slate-500 text-sm">No sources yet. Add the city's news RSS or official pages to monitor.</CardContent></Card>}
      </div>
      <Dialog open={!!importing} onOpenChange={(o) => !o && setImporting(null)}>
        <DialogContent data-testid="feed-import-dialog">
          <DialogHeader><DialogTitle className="font-serif text-[#1a2744]">Import item as draft</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input value={importForm.title} onChange={(e) => setImportForm((f) => ({ ...f, title: e.target.value }))} placeholder="Item title" className="h-11" data-testid="import-title-input" />
            <Textarea value={importForm.content} onChange={(e) => setImportForm((f) => ({ ...f, content: e.target.value }))} placeholder="Paste content here…" rows={6} data-testid="import-content-input" />
            <Button onClick={doImport} className="w-full h-11 bg-[#1a2744] text-white font-bold btn-press" data-testid="import-save-button">Save as Draft Story</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const DirectoryTab = () => (
  <AdminCrud
    title="Who Do I Call? Directory"
    listEndpoint="/directory" createEndpoint="/directory" updateEndpoint="/directory" deleteEndpoint="/directory"
    columns={[{ key: 'title', label: 'Title' }, { key: 'category', label: 'Category' }, { key: 'phone', label: 'Phone' }]}
    fields={[
      { name: 'category', label: 'Category', type: 'text', placeholder: 'City Services, Roads, Utilities…' },
      { name: 'title', label: 'Title', type: 'text' },
      { name: 'description', label: 'Description', type: 'textarea', rows: 2 },
      { name: 'phone', label: 'Phone', type: 'text' },
      { name: 'email', label: 'Email', type: 'text' },
      { name: 'url', label: 'Website', type: 'text' },
      { name: 'order', label: 'Sort order', type: 'number', default: 99 },
    ]}
  />
);

const SecurityTab = () => {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next !== confirm) { toast.error('New passwords do not match'); return; }
    setBusy(true);
    try {
      await api.post('/auth/change-password', { current_password: current, new_password: next });
      const u = getUser();
      if (u) localStorage.setItem('w8_user', JSON.stringify({ ...u, must_change_password: false }));
      toast.success('Password changed successfully');
      setCurrent(''); setNext(''); setConfirm('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Password change failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <Card><CardContent className="p-5">
        <p className="font-serif font-bold text-[#1a2744] mb-1 flex items-center gap-2"><KeyRound className="h-5 w-5 text-[#C9A84C]" /> Change your password</p>
        <p className="text-sm text-slate-500 mb-4">Use a strong password you have not used anywhere else. Minimum 8 characters.</p>
        <form onSubmit={submit} className="space-y-3 max-w-md" data-testid="security-change-password-form">
          <Input type="password" required value={current} onChange={(e) => setCurrent(e.target.value)} placeholder="Current password" className="h-11" data-testid="security-current-password" />
          <Input type="password" required minLength={8} value={next} onChange={(e) => setNext(e.target.value)} placeholder="New password" className="h-11" data-testid="security-new-password" />
          <Input type="password" required minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Confirm new password" className="h-11" data-testid="security-confirm-password" />
          <Button type="submit" disabled={busy} className="h-11 bg-[#1a2744] text-white font-bold btn-press" data-testid="security-change-password-submit">
            {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Change Password
          </Button>
        </form>
      </CardContent></Card>
      <Card><CardContent className="p-5">
        <p className="font-serif font-bold text-[#1a2744] mb-1 flex items-center gap-2"><ShieldPlus className="h-5 w-5 text-[#C9A84C]" /> Backup admin account</p>
        <p className="text-sm text-slate-500">Create a second admin so the campaign is never locked out. Use the <strong>Admin Users</strong> tab (or the Launch Readiness page) — choose the <strong>Admin</strong> role. New users must change their password on first login.</p>
      </CardContent></Card>
    </div>
  );
};

export default function AdminSettings() {
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get('tab') || 'site');
  const [settings, setSettings] = useState({});

  const load = () => api.get('/admin/settings').then((r) => setSettings(r.data || {}));
  useEffect(() => { load(); }, []);
  useEffect(() => { const t = searchParams.get('tab'); if (t) setTab(t); }, [searchParams]);

  return (
    <div data-testid="admin-settings">
      <h1 className="font-serif font-black text-2xl text-[#1a2744] mb-4">Settings</h1>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-white border border-slate-200 h-auto p-1 flex-wrap justify-start">
          {[['site', 'Site'], ['hope', 'Hope Theme'], ['video', 'Intro Video'], ['security', 'Security'], ['footer', 'Footer & Disclaimers'], ['social', 'Social Links'], ['sharing', 'Social Sharing'], ['users', 'Admin Users'], ['directory', 'Directory'], ['feeds', 'Source Monitoring']].map(([v, l]) => (
            <TabsTrigger key={v} value={v} className="h-10 px-4 font-bold data-[state=active]:bg-[#1a2744] data-[state=active]:text-white" data-testid={`settings-tab-${v}`}>{l}</TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
      <div className="mt-5">
        {tab === 'site' && (
          <SettingsForm settingKey="site" values={settings.site} onSaved={load} fields={[
            { name: 'title', label: 'Site title' },
            { name: 'subtitle', label: 'Site subtitle' },
            { name: 'slogan', label: 'Main slogan', type: 'textarea' },
            { name: 'secondary_slogan', label: 'Secondary slogan', type: 'textarea' },
            { name: 'campaign_line', label: 'Major campaign line', type: 'textarea' },
            { name: 'contact_email', label: 'Contact email' },
            { name: 'homepage_mode', label: 'Homepage mode', type: 'select', default: 'full', options: [
              { value: 'full', label: 'Full mode (all sections incl. video, gallery, videos)' },
              { value: 'launch', label: 'First-launch mode (campaign essentials only)' },
            ], help: 'First-launch mode hides the intro-video and gallery/video sections until real media is ready. Turn it on from here on launch day.' },
          ]} />
        )}
        {tab === 'security' && <SecurityTab />}
        {tab === 'hope' && <HopeTab />}
        {tab === 'video' && (
          <div className="space-y-4">
            <p className="text-sm text-slate-600 bg-white border border-slate-200 rounded-lg p-3">Controls the homepage <strong>"Watch Steve's Message to Ward 8"</strong> video and the first-visit popup. Paste any YouTube link format — watch, share (youtu.be), Shorts, or embed — it is converted to a safe embeddable player automatically.</p>
            <SettingsForm settingKey="intro_video" values={settings.intro_video} onSaved={load} fields={[
              { name: 'video_url', label: 'Homepage intro video URL', help: 'Any YouTube URL: youtube.com/watch?v=…, youtu.be/…, youtube.com/shorts/…, or an embed URL.' },
              { name: 'popup_enabled', label: 'Enable first-visit popup', type: 'select', default: 'on', options: [
                { value: 'on', label: 'ON — show popup to first-time visitors' },
                { value: 'off', label: 'OFF — never show the popup automatically' },
              ] },
              { name: 'popup_title', label: 'Popup title', help: 'Default: A Message from Steve' },
              { name: 'popup_subtitle', label: 'Popup subtitle', help: "Default: Watch Steve's Message to Ward 8" },
              { name: 'button_text', label: 'Homepage button text', help: 'Default: A MESSAGE FROM STEVE' },
              { name: 'supporting_text', label: 'Button supporting text', help: "Default: Watch Steve's Message to Ward 8" },
              { name: 'privacy_mode', label: 'YouTube privacy-enhanced mode', type: 'select', default: 'on', options: [
                { value: 'on', label: 'ON — youtube-nocookie.com (recommended)' },
                { value: 'off', label: 'OFF — standard youtube.com embed' },
              ] },
              { name: 'autoplay_muted', label: 'Autoplay in popup (muted only)', type: 'select', default: 'off', options: [
                { value: 'off', label: 'OFF — visitor presses Watch Video (recommended)' },
                { value: 'on', label: 'ON — autoplay muted when popup opens' },
              ], help: 'Autoplay is always muted. Sound never plays automatically.' },
              { name: 'show_in_hero', label: 'Also show button in hero banner', type: 'select', default: 'off', options: [
                { value: 'off', label: 'OFF' },
                { value: 'on', label: 'ON — add the button to the hero CTA area' },
              ] },
              { name: 'show_placeholder_public', label: 'If no video: show public placeholder', type: 'select', default: 'off', options: [
                { value: 'off', label: 'OFF — hide the section from the public (admins see a warning)' },
                { value: 'on', label: 'ON — show a clean placeholder message to visitors' },
              ] },
              { name: 'popup_version', label: 'Popup version / reset key', help: 'Visitors who closed the popup never see it again — unless you change this value (e.g. v1 → v2), which shows the popup to everyone once more. Default: v1' },
            ]} />
          </div>
        )}
        {tab === 'footer' && (
          <div className="space-y-4">
            <SettingsForm settingKey="footer" values={settings.footer} onSaved={load} fields={[
              { name: 'about', label: 'Footer about text', type: 'textarea' },
              { name: 'disclaimer', label: 'Campaign disclaimer', type: 'textarea' },
              { name: 'copyright', label: 'Copyright line' },
            ]} />
            <SettingsForm settingKey="disclaimers" values={settings.disclaimers} onSaved={load} fields={[
              { name: 'allegations', label: 'Allegations wording disclaimer', type: 'textarea' },
              { name: 'action_hub', label: 'Issue Tracker disclaimer', type: 'textarea' },
            ]} />
          </div>
        )}
        {tab === 'social' && (
          <SettingsForm settingKey="social_links" values={settings.social_links} onSaved={load} fields={[
            { name: 'facebook', label: 'Facebook URL' },
            { name: 'youtube', label: 'YouTube URL' },
            { name: 'rumble', label: 'Rumble URL' },
            { name: 'x_twitter', label: 'X (Twitter) URL' },
            { name: 'instagram', label: 'Instagram URL' },
          ]} />
        )}
        {tab === 'sharing' && <SharingTab values={settings.social_sharing} onSaved={load} />}
        {tab === 'users' && <UsersTab />}
        {tab === 'directory' && <DirectoryTab />}
        {tab === 'feeds' && <FeedsTab />}
      </div>
    </div>
  );
}
